// ═══════════════════════════════════════════════════
//  Wood Finishing Cost
//
//  The single implementation, shared by the server cost helpers and the client
//  cost preview. Both call it rather than mirroring the arithmetic — the same
//  arrangement calcComponentCncCost and generateFabricPieces already use.
// ═══════════════════════════════════════════════════

import type { PanelFinish } from '../schemas/panel-finish.schema';

/** A finish profile reduced to what costing needs. */
export interface FinishRate {
  id: string;
  name: string;
  /** Materials recipe + cost items, per square foot of finished face. */
  cost_per_sqft: number;
}

/** One kind of framing panel, with its per-piece area and piece count. */
export interface FinishablePanel {
  name: string;
  /** Area of ONE piece, in square feet. */
  sqft_each: number;
  /** How many pieces of this panel the component contains. */
  pieces: number;
  finish: PanelFinish;
}

/** Geometry a component type supplies so its framing can be enumerated. */
export interface FinishablePanelSource {
  sectionCount: number;
  ribCount: number;
  /** Area of ONE side panel, in square feet. */
  sidePanelSqftEach: number;
  /** Area of ONE rib, in square feet. */
  ribSqftEach: number;
  /**
   * Inches that a single width-panel piece runs. The run axis differs by
   * component — section length for bases and cushions, depth for arms — so the
   * caller supplies it rather than this module guessing.
   */
  railRunLengthIn: number;
}

export interface WoodFinishRow {
  panel_name: string;
  pieces: number;
  /** Total face area actually finished: sqft_each x pieces x sides. */
  sqft: number;
  sides: 1 | 2;
  finish_id: string | null;
  finish_name: string | null;
  cost_per_sqft: number;
  cost: number;
  complexity: number;
  warning?: string;
}

export interface WoodFinishResult {
  cost: number;
  rows: WoodFinishRow[];
  /** Σ (area x sides x complexity) — feeds weight_bq_finish_sqft. */
  finished_sqft_weighted: number;
  /** Σ (pieces x complexity) — feeds weight_bq_finish_panel. */
  finished_panel_weighted: number;
  /** True if any panel is ticked, regardless of whether a profile resolved. */
  any_finished: boolean;
}

/**
 * Enumerate every finish-enabled panel in a framing config.
 *
 * Piece counts match the captions the template editor already prints, so the
 * per-panel complexity weight counts what the user sees. Total area is
 * unaffected by that choice — a rail billed as one long piece and as N section
 * pieces cover the same square footage.
 */
export function collectFinishablePanels(
  framing: any,
  src: FinishablePanelSource,
): FinishablePanel[] {
  const out: FinishablePanel[] = [];
  const push = (name: string, sqftEach: number, pieces: number, finish: any) => {
    if (!finish?.enabled) return;
    if (sqftEach <= 0 || pieces <= 0) return;
    out.push({ name, sqft_each: sqftEach, pieces, finish });
  };

  push('Side Panel', src.sidePanelSqftEach, 2 * src.sectionCount, framing?.side_panel_finish);
  push('Rib', src.ribSqftEach, src.ribCount, framing?.rib_finish);

  for (const rail of framing?.rails ?? []) {
    const sqftEach = ((rail.width ?? 0) * src.railRunLengthIn) / 144;
    push(rail.name ?? 'Panel', sqftEach, src.sectionCount, rail.finish);
  }

  for (const panel of framing?.additional_panels ?? []) {
    const sqftEach = ((panel.width ?? 0) * (panel.height ?? 0)) / 144;
    const pieces = (panel.qty_per_section ?? 1) * src.sectionCount;
    push(panel.name ?? 'Panel', sqftEach, pieces, panel.finish);
  }

  return out;
}

/**
 * Cost the finished panels and produce the complexity metrics.
 *
 * No waste factor is applied. Framing material carries one because whole sheets
 * are bought and offcuts discarded; finish goes on the parts that are kept.
 *
 * The complexity multiplier deliberately does not touch cost — a panel with
 * more planing and cutting takes more time, not more lacquer.
 */
export function computeWoodFinish(
  panels: FinishablePanel[],
  defaultFinishId: string | null | undefined,
  finishRates: Map<string, FinishRate>,
): WoodFinishResult {
  const rows: WoodFinishRow[] = [];
  let cost = 0;
  let sqftWeighted = 0;
  let panelWeighted = 0;

  for (const panel of panels) {
    const sides = panel.finish.sides === 2 ? 2 : 1;
    const complexity = panel.finish.complexity ?? 1;
    const sqft = panel.sqft_each * panel.pieces * sides;

    const resolvedId = panel.finish.finish_id ?? defaultFinishId ?? null;
    const rate = resolvedId ? finishRates.get(resolvedId) ?? null : null;
    const costPerSqft = rate?.cost_per_sqft ?? 0;
    const rowCost = sqft * costPerSqft;

    cost += rowCost;
    // Metrics accrue whether or not a profile resolved: the work happens
    // regardless of whether the rate has been filled in yet.
    sqftWeighted += sqft * complexity;
    panelWeighted += panel.pieces * complexity;

    rows.push({
      panel_name: panel.name,
      pieces: panel.pieces,
      sqft,
      sides,
      finish_id: resolvedId,
      finish_name: rate?.name ?? null,
      cost_per_sqft: costPerSqft,
      cost: rowCost,
      complexity,
      ...(rate ? {} : { warning: 'no finish profile' }),
    });
  }

  return {
    cost,
    rows,
    finished_sqft_weighted: sqftWeighted,
    finished_panel_weighted: panelWeighted,
    any_finished: panels.length > 0,
  };
}

/**
 * Reduce finish profiles to the rate map the costing functions take.
 *
 * Typed structurally rather than as `Finish` so this module does not take a
 * schema dependency for a two-line helper — a `Finish[]` satisfies it as-is.
 *
 * `cost_per_sqft` is computed on the entity from materials plus cost items and
 * can be null on a profile with neither, so it is coerced once here rather than
 * at every read.
 */
export function finishRateMap(
  finishes: readonly { id: string; name: string; cost_per_sqft?: number | null }[],
): Map<string, FinishRate> {
  return new Map(
    finishes.map((f) => [
      f.id,
      { id: f.id, name: f.name, cost_per_sqft: Number(f.cost_per_sqft ?? 0) },
    ]),
  );
}
